import axiosInstance from '../../api/axiosInstance';
import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Gamepad2, PlusCircle, Pencil, Trash2, Clock, Hash, ShieldCheck, X } from 'lucide-react';

function Games() {
  const [submit, setSubmit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [gameInfo, setGameInfo] = useState({ name: '', code: '', resultTime: '', resultShowTime: '', showNumber: '' });
  const [games, setGames] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => { getGames(); }, []);

  const getGames = async () => {
    try {
      const result = await axiosInstance.get('/getgames');
      if (result?.data) setGames(result.data);
      else setGames([]);
    } catch { toast.error('Error fetching games'); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGameInfo(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setGameInfo({ name: '', code: '', resultTime: '', resultShowTime: '', showNumber: '' });
    setSubmit(true);
    setIsFormOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/addgame', gameInfo);
      toast.success('Game added successfully');
      getGames();
      resetForm();
    } catch { toast.error('Error adding game'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this game?')) return;
    try {
      await axiosInstance.delete('/deletegame', { data: { id } });
      toast.success('Game deleted');
      getGames();
    } catch { toast.error('Error deleting game'); }
  };

  const handleUpdate = (idx) => {
    // idx is actually the game object in this case if we map over sorted array, wait...
    setSubmit(false);
    setGameInfo(idx);
    setIsFormOpen(true);
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.put('/updategame', gameInfo);
      toast.success('Game updated');
      getGames();
      resetForm();
    } catch { toast.error('Error updating game'); }
    finally { setLoading(false); }
  };

  const formatTime = (time) => {
    if (!time) return 'â€”';
    let [hour, minute] = time.split(':').map(Number);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${String(minute).padStart(2, '0')} ${ampm}`;
  };

  return (
    <>
      <div className="page-header">
        <h2>Games</h2>
        <p>Manage all Satta King games” add, edit, or remove games.</p>
      </div>

      {/* â”€â”€ Form Card â”€â”€ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-title">
            <Gamepad2 size={16} /> Game Management
          </div>
          {!isFormOpen && (
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsFormOpen(true); }}>
              <PlusCircle size={15} style={{ marginRight: '6px' }} /> Create New Game
            </button>
          )}
        </div>
        
        {isFormOpen && (
          <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--bg-card)' }}>
            <div className="card-title" style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
              {submit ? 'Creating New Game' : 'Updating Game Details'}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={resetForm}>
              <X size={13} /> Cancel
            </button>
          </div>
        )}

        {isFormOpen && (
        <div className="card-body">
          <form onSubmit={submit ? handleSubmit : submitUpdate}>
            <div className="form-grid form-grid-cols-3">
              <div className="form-group">
                <label className="form-label">Game Name</label>
                <input name="name" value={gameInfo.name} onChange={handleChange} className="form-input" placeholder="e.g. Faridabad" required />
              </div>
              <div className="form-group">
                <label className="form-label">Game Code</label>
                <input name="code" value={gameInfo.code} onChange={handleChange} className="form-input" placeholder="e.g. FAR" required />
              </div>
              <div className="form-group">
                <label className="form-label">Show Order #</label>
                <input name="showNumber" type="number" value={gameInfo.showNumber} onChange={handleChange} className="form-input" placeholder="e.g. 1" required />
              </div>
              <div className="form-group">
                <label className="form-label">Result Time</label>
                <input name="resultTime" type="time" value={gameInfo.resultTime} onChange={handleChange} className="form-input" required />
              </div>
              <div className="form-group">
                <label className="form-label">Result Show Time</label>
                <input name="resultShowTime" type="time" value={gameInfo.resultShowTime} onChange={handleChange} className="form-input" required />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button type="submit" className={`btn ${submit ? 'btn-primary' : 'btn-success'}`} disabled={loading}>
                {submit ? <PlusCircle size={15} /> : <ShieldCheck size={15} />}
                {loading ? 'Saving...' : submit ? 'Add Game' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>

      {/* â”€â”€ Games Table â”€â”€ */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><Gamepad2 size={16} /> All Games ({games.length})</div>
        </div>
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th><Hash size={12} style={{ display: 'inline', marginRight: 4 }} />Order</th>
                <th>Game Name</th>
                <th>Code</th>
                <th><Clock size={12} style={{ display: 'inline', marginRight: 4 }} />Result Time</th>
                <th><Clock size={12} style={{ display: 'inline', marginRight: 4 }} />Show Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {games.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0' }}>No games added yet</td></tr>
              ) : (
                games
                  .sort((a, b) => a.showNumber - b.showNumber)
                  .map((item, idx) => (
                    <tr key={item._id}>
                      <td><span className="badge badge-purple">{item.showNumber}</span></td>
                      <td style={{ fontWeight: 600 }}>{item.name}</td>
                      <td><span className="badge badge-blue">{item.code}</span></td>
                      <td style={{ color: 'var(--text-secondary)' }}>{formatTime(item.resultTime)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{formatTime(item.resultShowTime)}</td>
                      <td><span className="badge badge-green"><span className="badge-dot pulse" />Active</span></td>
                      <td>
                        <div className="action-cell">
                          <button className="btn btn-warning btn-sm" onClick={() => handleUpdate(item)}><Pencil size={13} /> Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item._id)}><Trash2 size={13} /> Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default Games;
